// src/components/Login/Login.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Login.css";
import logo from "../../assets/dr-is-in.png";

const Login: React.FC = () => {
  const year = new Date().getFullYear();
  const navigate = useNavigate();
  const [learnOpen, setLearnOpen] = useState(false);

  useEffect(() => {
    if (!learnOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLearnOpen(false);
    };

    document.body.classList.add("login-modal-open");
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("login-modal-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [learnOpen]);

  return (
    <div className="login-container">
      <div className="login-box">
        <img src={logo} alt="Doctor Is In Logo" className="login-logo" />
        <h2 className="login-title">Doctor Is In App™</h2>
        <div className="login-tagline">AI Wellness Advisor with a human touch.</div>

        <div className="login-actions">
          <Link to="/student-login" className="login-link"><button type="button" className="login-btn">Student Login</button></Link>
          <Link to="/doctor-login" className="login-link"><button type="button" className="login-btn">Advisor Login</button></Link>
          <Link to="/parent-teacher-login" className="login-link"><button type="button" className="login-btn">Parent / Teacher Login</button></Link>
          <Link to="/school-admin" className="login-link"><button type="button" className="login-btn">School Admin Login</button></Link>
          <Link to="/admin-login" className="login-link"><button type="button" className="login-btn">Admin Login</button></Link>

          <button type="button" className="login-btn" onClick={() => navigate("/registration-portals")}>Registration Portals</button>
          <button type="button" className="login-btn" onClick={() => navigate("/contact-us")}>Contact Us</button>
          <button type="button" className="login-btn login-learn-btn" onClick={() => setLearnOpen(true)}>Learn More</button>
        </div>

        <div className="login-footer">© {year} Doctor Is In App. All rights reserved.</div>
      </div>

      {learnOpen && (
        <div
          className="login-learn-overlay"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setLearnOpen(false);
          }}
        >
          <section className="login-learn-modal" role="dialog" aria-modal="true" aria-labelledby="learn-more-title">
            <button className="login-learn-close" type="button" onClick={() => setLearnOpen(false)} aria-label="Close Learn More">×</button>

            <header className="login-learn-hero">
              <img src={logo} alt="Doctor Is In App" className="login-learn-logo" />
              <div className="login-learn-badge">GENESIS 1.0 • BETA 2026</div>
              <h2 id="learn-more-title">Learn About Doctor Is In App™</h2>
              <p>
                A school wellness ecosystem combining Casey™ AI guidance, live wellness advisors,
                AURA™ intelligence, multilingual support, school resources, and human-centered escalation pathways.
              </p>
            </header>

            <div className="login-learn-section-title"><span>What the platform delivers</span></div>

            <div className="login-learn-grid">
              <article className="login-learn-card">
                <h3>🌟 Meet Casey™</h3>
                <p>Casey is the AI Wellness Advisor built to support emotional wellbeing, school life, healthy decision-making, digital safety, substance-misuse prevention, and help-seeking.</p>
              </article>

              <article className="login-learn-card">
                <h3>🩺 What Casey Covers</h3>
                <ul>
                  <li>Stress, anxiety, sadness, grief, and loneliness</li>
                  <li>Bullying, school pressure, trauma, and safety concerns</li>
                  <li>Vaping, drugs, alcohol, medication misuse, and peer pressure</li>
                  <li>Digital influence, cyberbullying, viral challenges, and misinformation</li>
                  <li>Parent and teacher scripts, guidance, and student support ideas</li>
                </ul>
              </article>

              <article className="login-learn-card">
                <h3>👥 Live Advisor Support</h3>
                <p>Students, parents, and teachers can request approved human support when additional guidance, reassurance, or follow-up is needed.</p>
              </article>

              <article className="login-learn-card">
                <h3>🌦️ AURA™ Emotional Weather</h3>
                <p>AURA helps schools recognize anonymous wellness trends, emerging concerns, crisis signals, and changes in emotional climate across schools and districts.</p>
              </article>

              <article className="login-learn-card">
                <h3>🌎 Multi-Language Access</h3>
                <p>Doctor Is In App™ helps reduce language barriers by supporting conversations across multiple languages for students, families, educators, and advisors.</p>
              </article>

              <article className="login-learn-card">
                <h3>🔒 Confidential, School-Guided Support</h3>
                <p>The platform is designed for safe, supportive, judgment-free wellness guidance within school-managed access and escalation procedures.</p>
              </article>
            </div>

            <div className="login-learn-section-title"><span>Trust, safety, and recognition roadmap</span></div>

            <div className="login-learn-grid">
              <article className="login-learn-card login-learn-card-trust">
                <h3>🛡️ Privacy & Student Protection</h3>
                <ul>
                  <li>COPPA compliance preparation</li>
                  <li>FERPA-aligned student-data safeguards</li>
                  <li>Role-based access and school-managed accounts</li>
                  <li>Data minimization and safeguarding procedures</li>
                  <li>Accessibility and cybersecurity review planning</li>
                </ul>
              </article>

              <article className="login-learn-card login-learn-card-trust">
                <h3>⭐ Independent Review Roadmap</h3>
                <p>Doctor Is In App™ is preparing materials for educational, child-safety, privacy, accessibility, and responsible-AI review, including planned outreach to recognized education and family-trust organizations such as Common Sense Media.</p>
                <p className="login-learn-small">Formal certifications, seals, ratings, and endorsements will be displayed only after they are awarded.</p>
              </article>
            </div>

            <div className="login-learn-section-title"><span>What comes next</span></div>

            <div className="login-learn-grid">
              <article className="login-learn-card login-learn-card-roadmap">
                <h3>🌍 Global Beta Initiative</h3>
                <p>The beta program is being prepared to validate the platform in real educational settings, gather structured feedback, and strengthen support for students, families, educators, and school leaders.</p>
              </article>

              <article className="login-learn-card login-learn-card-roadmap">
                <h3>🚀 Version 1.1</h3>
                <p>Planned enhancements include AURA Pulse™, expanded emotional wellness trend intelligence, and AURA SOS™, an emergency support and escalation capability designed to connect users with approved human assistance when urgent intervention may be needed.</p>
              </article>
            </div>

            <div className="login-learn-notice">
              <strong>Professional Notice:</strong> Casey™ is an AI Wellness Advisor designed to provide educational wellness guidance and emotional support. Casey does not diagnose medical or mental health conditions, prescribe treatment, create a clinician-patient relationship, replace licensed professionals, or replace emergency services.
            </div>

            <div className="login-learn-status">
              <strong>Current status:</strong> Beta validation, compliance documentation, safeguarding procedures, accessibility review, responsible-AI governance, and independent-review materials are in preparation.
            </div>

            <div className="login-learn-mission">Safe. Trusted. Compassionate. Always here.</div>
          </section>
        </div>
      )}
    </div>
  );
};

export default Login;
